import { formatDayMonth } from '@helpers/date';
/**
 * The words the referral review queue puts on screen, kept apart from the page so they can be
 * tested without rendering it. The backend speaks in check names and counts; a reviewer deciding
 * whether money moves needs sentences.
 */

export interface ReferralCheck {
  name: string;
  passed: boolean | null;
}

export interface ReferralRelevance {
  /** FIT, UNCLEAR or NOT_FIT. */
  verdict: string | null;
  matchedTerms: string[] | null;
  negativeTerms: string[] | null;
  industries: string[] | null;
  score: number | null;
  /** 0 means only the profile was scored, which is what a scanned CV looks like. */
  cvTextChars: number | null;
}

export interface ReferralFlags {
  referredLast24h: number | null;
  referredLast7d: number | null;
  payoutUpiSharedWith: number | null;
  signupIpHash: string | null;
  sameIpReferred: number | null;
  minutesSincePreviousReferral: number | null;
}

/**
 * The twelve gates in the order a reviewer reads them: where the people are, who they are, what
 * they did, and last whether they belong here at all.
 *
 * phoneVerified says "not required yet" because the owner has switched OTP off: the gate is
 * recorded but does not hold a reward back, and a red chip without that note reads as a reason
 * to reject.
 */
export const CHECK_LABELS: Record<string, string> = {
  indianResident: 'Lives in India',
  referrerIndian: 'Referrer in India',
  notSelf: 'Not a self referral',
  phonePresent: 'Phone number given',
  phoneVerified: 'Phone verified (not required yet)',
  phoneUnique: 'Phone not rewarded before',
  emailVerified: 'Email verified',
  applied: 'Applied to a job',
  profileComplete: 'Profile complete',
  categorySelected: 'Job category chosen',
  locationSelected: 'City given',
  cvPresent: 'CV uploaded',
  cvReadable: 'CV readable',
  industryFit: 'Works in our industries',
};

const CHECK_ORDER = Object.keys(CHECK_LABELS);

/** A check's label; a name the backend adds later shows as itself rather than vanishing. */
export const checkLabel = (name: string) => CHECK_LABELS[name] ?? name;

/** Known checks in reading order, then anything unknown in the order it arrived. */
export const orderedChecks = (checks: ReferralCheck[] | null | undefined): ReferralCheck[] => {
  const list = (checks ?? []).filter((c) => c && c.name);
  const rank = (n: string) => {
    const i = CHECK_ORDER.indexOf(n);
    return i < 0 ? CHECK_ORDER.length : i;
  };
  return [...list].sort((a, b) => rank(a.name) - rank(b.name));
};

/**
 * A scanned CV has no text layer, so the industry check could only score the profile. The
 * backend sends it to review rather than refusing it; the reviewer has to open the file.
 */
export const isScannedCv = (checks: ReferralCheck[] | null | undefined, relevance: ReferralRelevance | null | undefined) =>
  (checks ?? []).some((c) => c.name === 'cvReadable' && c.passed === false) ||
  (relevance != null && relevance.cvTextChars === 0);

export const VERDICT_LABELS: Record<string, string> = {
  FIT: 'Industry fit',
  UNCLEAR: 'Unclear fit',
  NOT_FIT: 'Not a fit',
};

/** CRMStatusChip keys, so the verdict shares the console's green, amber and red. */
export const VERDICT_TONE: Record<string, string> = {
  FIT: 'success',
  UNCLEAR: 'needsreview',
  NOT_FIT: 'failed',
};

export const verdictLabel = (v: string | null | undefined) => (v ? VERDICT_LABELS[v] ?? v : 'Not scored');

/** What a reward's status means to whoever is paying it. */
export const REWARD_STATUS_LABELS: Record<string, string> = {
  PENDING_REVIEW: 'Under review',
  EARNED: 'Approved, to pay',
  PAID: 'Paid',
  REJECTED: 'Rejected',
  COVERED: 'Covered by earlier rewards',
};

export const rewardStatusLabel = (s: string | null | undefined) => (s ? REWARD_STATUS_LABELS[s] ?? s : '');

/** A referred person's status as their referrer sees it (myReferrals.people.status). */
export const PERSON_STATUS_LABELS: Record<string, string> = {
  PENDING: 'In progress',
  UNDER_REVIEW: 'Under review',
  APPROVED: 'Approved',
  PAID: 'Paid',
  NOT_ELIGIBLE: 'Not eligible',
  COUNTED: 'Counts toward milestones',
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "4 minutes", "3 hours", "2 days": the gap since the previous referral, at a readable grain. */
export const gapText = (minutes: number) => {
  if (minutes < 60) return plural(minutes, 'minute', 'minutes');
  if (minutes < 48 * 60) return plural(Math.round(minutes / 60), 'hour', 'hours');
  return plural(Math.round(minutes / (24 * 60)), 'day', 'days');
};

export interface FlagLine {
  text: string;
  /** True when the number is worth a second look; the reviewer still decides. */
  warn: boolean;
}

/*
 * Where a number starts to deserve attention. The 24h and 7d counts include this referral, so one
 * in a day is normal; a gap under an hour between two sign-ups is how a referrer filling in forms
 * for people looks. None of these blocks anything, by the owner's decision: they draw the eye.
 */
const BURST_24H = 2;
const BURST_7D = 5;
const QUICK_GAP_MINUTES = 60;

/** The flags as sentences, worth-a-look first. Zero and missing values say nothing. */
export const flagLines = (f: ReferralFlags | null | undefined): FlagLine[] => {
  if (!f) return [];
  const out: FlagLine[] = [];
  const n = (v: number | null | undefined) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  if (n(f.payoutUpiSharedWith) > 0) {
    out.push({ text: `UPI shared with ${plural(n(f.payoutUpiSharedWith), 'other referrer', 'other referrers')}`, warn: true });
  }
  if (n(f.sameIpReferred) > 0) {
    out.push({ text: `${plural(n(f.sameIpReferred), 'other referral', 'other referrals')} from the same network`, warn: true });
  }
  if (n(f.referredLast24h) > 0) {
    out.push({ text: `${plural(n(f.referredLast24h), 'referral', 'referrals')} in 24h`, warn: n(f.referredLast24h) >= BURST_24H });
  }
  if (n(f.referredLast7d) > 0) {
    out.push({ text: `${plural(n(f.referredLast7d), 'referral', 'referrals')} in 7 days`, warn: n(f.referredLast7d) >= BURST_7D });
  }
  const gap = f.minutesSincePreviousReferral;
  if (typeof gap === 'number' && Number.isFinite(gap) && gap >= 0) {
    out.push({ text: `Referred ${gapText(gap)} after the previous one`, warn: gap < QUICK_GAP_MINUTES });
  }
  return [...out.filter((l) => l.warn), ...out.filter((l) => !l.warn)];
};

/** One row of getReferralRewards. */
export interface RewardRow {
  id: string;
  referrerType: string;
  referrerId: string;
  referrerName: string | null;
  referrerEmail: string | null;
  /** The person whose qualification earned this row. */
  referredId: string;
  referredName: string;
  amount: number;
  currency: string;
  /** PENDING_REVIEW, EARNED, PAID or REJECTED. */
  status: string;
  earnedAt: string | null;
  paidAt: string | null;
  /** Who marked it paid. */
  paidBy?: string | null;
  paymentRef: string | null;
  /** Where the referrer asked to be paid; either may be missing. */
  payoutUpiId: string | null;
  payoutPhone: string | null;
  rejectedReason?: string | null;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  checks?: ReferralCheck[] | null;
  relevance?: ReferralRelevance | null;
  flags?: ReferralFlags | null;
  completenessAtQualification?: number | null;
  resumeChars?: number | null;
  /** PER_PERSON, TIER, PLACEMENT or DRAW. Absent on a backend older than programme v3. */
  kind?: string | null;
  /** The credit behind a per-person or placement row. */
  creditId?: string | null;
  threshold?: number | null;
  tierAmount?: number | null;
  legacyNetted?: number | null;
  contributors?: Contributor[] | null;
  applicationId?: string | null;
  drawId?: string | null;
  month?: string | null;
}

/** A friend counted toward a milestone. */
export interface Contributor {
  creditId: string;
  referredId: string | null;
  name: string | null;
  excluded: boolean;
}

export type RewardKind = 'PER_PERSON' | 'TIER' | 'PLACEMENT' | 'DRAW';

export const rewardKind = (r: Pick<RewardRow, 'kind'>): RewardKind =>
  r.kind === 'TIER' || r.kind === 'PLACEMENT' || r.kind === 'DRAW' ? r.kind : 'PER_PERSON';

/** Whether a row is about one referred person (so has a CV, checks and evidence to show). */
export const hasPerson = (r: Pick<RewardRow, 'kind'>) => {
  const k = rewardKind(r);
  return k === 'PER_PERSON' || k === 'PLACEMENT';
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September',
  'October', 'November', 'December'];

/** "October 2026" from "2026-10"; the input as given when it is not that shape. */
export const monthLabel = (ym: string | null | undefined) => {
  const m = /^(\d{4})-(\d{2})$/.exec(ym ?? '');
  return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1]}` : (ym ?? '');
};

/** What a reward row is for, in one line, before any names. */
export const rewardTitle = (r: RewardRow) => {
  switch (rewardKind(r)) {
    case 'TIER':
      // An ambassador counts credits (a priority-role candidate is two), not friends.
      return r.referrerType === 'AMBASSADOR'
        ? `Milestone: ${r.threshold ?? '?'} credits`
        : `Milestone: ${r.threshold ?? '?'} qualified friends`;
    case 'PLACEMENT':
      return `Placement: ${r.referredName || (r.referrerType === 'AMBASSADOR' ? 'their candidate' : 'their friend')} was hired`;
    case 'DRAW':
      return `Monthly draw: ${monthLabel(r.month)}`;
    default:
      return r.referredName || r.referredId;
  }
};

/* Kept importable from here, where the review queue and workspace have always found it. */
export { referrerLabel, referrerPath } from './referrer';

/** One row of getReferredJobSeekers: a person who signed up through a link, and who referred them. */
export interface ReferredSeekerRow {
  jobSeekerId: string;
  name: string | null;
  email: string | null;
  joinedAt: string | null;
  qualified: boolean;
  qualifiedAt: string | null;
  code: string;
  referrerType: string;
  referrerId: string;
  referrerName: string | null;
  referrerEmail: string | null;
  /** Null once qualified. */
  pendingReason?: string | null;
  statusReason?: string | null;
  checks?: ReferralCheck[] | null;
}

/** "UPI ravi@okaxis · 98765 43210", or null when the referrer has not said. */
export const payoutLine = (r: { payoutUpiId?: string | null; payoutPhone?: string | null }) => {
  const parts = [
    r.payoutUpiId ? `UPI ${r.payoutUpiId}` : null,
    r.payoutPhone ? `Phone ${r.payoutPhone}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : null;
};

/*
 * One line for the "Referred job seekers" status column. Qualifying is not the end: a Super Admin
 * can still reject or pay the reward, and the backend says so in statusReason ("Rejected: <why>",
 * "Paid"). This used to print "Qualified <date>" for anyone with qualifiedAt and never read
 * statusReason once they qualified, so a rejected referral read as a good one here while the
 * referrer's own card said "Not eligible".
 */
export const referredStatusLine = (r: Pick<ReferredSeekerRow, 'qualified' | 'qualifiedAt' | 'statusReason'>) => {
  const reason = r.statusReason?.trim();
  if (r.qualified && (!reason || reason === 'Qualified')) {
    return `Qualified${r.qualifiedAt ? ` ${formatDayMonth(r.qualifiedAt)}` : ''}`;
  }
  return reason || 'Not yet qualified';
};
